import type { StoredPopoutBody } from '../../hooks/useTeamTodaySurface';

/*
 * The stored popout body's renderer (pregeneration build spec §5) — the v2 half of the popout
 * split. Renders `sections.popoutBodies[conversationId]` ({shape, body, facts}, composed at
 * BRIEF time by the MVP's composer v2): no fetch, instant open. The legacy per-click renderer
 * (PopoutBody.tsx BriefPopoutBody + the POST hook) stays untouched for briefs stored without
 * the key and is deleted with the v1 path in PR C.
 *
 * Layout (round-6 mock, rulings 2-9):
 *  - ONE prose channel: the body's FIRST SENTENCE takes the lede register typographically
 *    (16.5px); the rest is body prose. No standfirst field, no self-echo channel.
 *  - `facts` is an UNLABELLED list — SECTION_LABELS is never consulted, no heading is ever
 *    stamped, and each fact's `from` (provenance) is never rendered.
 *  - Empty body → bare null (P7 `empty:hidden` invariant: the dialog's body zone must collapse
 *    whole, so this renderer must not emit wrapper chrome around nothing).
 *
 * LEGIBILITY (phase-4, blocks-lane STEP 1). Ryan, 2026-08-08: "not seeing the more dynamic
 * generation at all" — on a brief where 26 of 35 bodies DID choose `prose_and_facts`. Both halves
 * were true, because the structure was styled to disappear: a fact rendered at the same 15px and
 * the same token as the body's second paragraph, separated only by a 4px grey dot and a 12px gap,
 * while each entry is itself one-or-two complete sentences (105-295 chars, median 199) — so the
 * majority of the popout was a list written as prose and styled as prose. Structure written as
 * prose and styled as prose cannot read as structure, whatever `shape` says.
 *
 * Two cues fix that, and neither adds a WORD (the schema's binding rule: the renderer may pick a
 * medium, never author text — SECTION_LABELS stamping a `kind` tag is the defect that killed the
 * old schema):
 *   1. the MARK — an en-dash in the accent, which is the in-product precedent one surface over
 *      (the note page's `.nd-ul li::before{content:'–'; color:var(--acc)}`), with a real hanging
 *      indent so wrapped lines align under the text rather than under the mark;
 *   2. the REGISTER — a genuine step down in size and leading from body prose, so the block reads
 *      as a tight list of specifics rather than a third paragraph.
 * Deliberately NOT used: any heading or label (renderer-authored text), allcaps or letterspaced
 * mono (standing design rule — and the note page's own `.nd-sub` breaks it, so it is not the
 * precedent to copy here), and the overhaul token family (`--ink/--hair/--acc`): this renders
 * inside the `.rd` popover, so the accent comes from `text-acc` on the `--rd-*` side. That also
 * retires the two hardcoded dot literals the old mark carried.
 *
 * Color (bug-615 token pin, spec §5): the lede, the body, and the facts all CARRY content →
 * `text-prose-foreground`. `text-muted-foreground` (3.45:1 on the cream popover, under AA)
 * stays chrome-only. These class strings are pinned by name in the prose-contrast suite (§9) —
 * a restyle that does not update the pin leaves a stale pin that still passes.
 */

/** First sentence split for the lede register. A miss (no terminal punctuation followed by a
 * sentence opener) keeps the whole body in the lede — a wrong split only moves a sentence
 * between two readable registers, so the rule stays simple on purpose. */
export function splitLede(body: string): { lede: string; rest: string | null } {
  const m = body.match(/^([\s\S]*?[.!?][)"'”’]?)\s+(?=["'“‘(]?[A-Z0-9])([\s\S]+)$/);
  if (!m) return { lede: body, rest: null };
  return { lede: m[1], rest: m[2] };
}

export function StoredPopoutBodyView({ stored }: { stored: StoredPopoutBody }) {
  const body = (stored.body ?? '').trim();
  const facts = (stored.facts ?? []).filter(f => !!f?.text?.trim());
  if (!body) return null;
  const { lede, rest } = splitLede(body);

  return (
    <div className="brief-stored-popout-body" data-testid="stored-popout-body">
      <p className="text-[16.5px] leading-[1.55] text-prose-foreground">{lede}</p>
      {rest && <p className="mt-2.5 text-[15px] leading-[1.6] text-prose-foreground">{rest}</p>}
      {facts.length > 0 && (
        <ul className="mt-4 space-y-3" data-testid="stored-popout-facts">
          {facts.map((fact, i) => (
            <li key={i} className="flex gap-2.5 text-[14.5px] leading-[1.5] text-prose-foreground">
              {/* The mark, not a label: an en-dash in the accent (the note page's list precedent),
                * width-fixed so every entry's text hangs on one edge. */}
              <span aria-hidden="true" className="w-2 shrink-0 text-acc">
                –
              </span>
              <span className="min-w-0 flex-1">{fact.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
